import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { PageHeader } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { MEAL_TABS, MealTab } from '@/constant/app-paths';
import { useUrlTab } from '@/hooks/use-linked-record';
import { useToast } from '@/hooks/use-toast';
import { formatDateOnly } from '@/lib/utils/custom-date/custom-date';
import { SoonBadge, SoonPanel } from '@/modules/settings';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { ExportCsvButton } from '@/modules/service-requests/shared/components/export-csv-button';
import { downloadCsv } from '@/lib/csv';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { Printer } from 'lucide-react';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useIsMobile } from '@/hooks/use-media-query';
import { useTranslation } from 'react-i18next';
import { useDepartmentOptions, useEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import { createFoodRequestsTableColumns } from '../../components/food-requests-table/food-requests-table-columns';
import { DEFAULT_LUNCH_REQUESTS_ORDER } from '../../graphql/queries';
import { useGetLunchRequests } from '../../hooks/use-manage-lunch';
import { getAllLunchRequests } from '../../services/manage-lunch.service';
import { LunchRequestsPagingResult } from '../../types/manage-lunch.types';
import { buildFoodRequestsCsv } from '../../utils/food-requests-csv';
import { buildFoodRequestsPrintHtml, printFoodRequests } from '../../utils/food-requests-print';
import { buildLunchDayFilter, todayInOrganization } from '../../utils/lunch-date';

/** The table's filter for one day's food requests, shared by the table and the CSV export. */
const dayFilterOf = (date: string) => {
  const dayClause = buildLunchDayFilter(date);
  return dayClause ? { RequestedForDate: dayClause } : {};
};

const FoodRequestsTab = ({
  date,
  mobileActions,
}: {
  date: string;
  /** On a phone, the day picker on the cards' line, beside the filter button. */
  mobileActions?: ReactNode;
}) => {
  const { t } = useTranslation();
  const { pagination, resetPage, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_LUNCH_REQUESTS_ORDER,
  });

  // A new day starts over from the first page.
  useEffect(() => {
    resetPage();
  }, [date, resetPage]);

  const { data, isLoading, error } = useGetLunchRequests({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere(dayFilterOf(date), columnWhere),
    order,
  });
  const result = (data as { LunchRequests?: LunchRequestsPagingResult })?.LunchRequests;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { byUserId } = useEmployeesByUserIds(items.map((request) => request.UserId));
  const { data: departments } = useDepartmentOptions();
  const columns = useMemo(
    () =>
      createFoodRequestsTableColumns({
        t,
        emailOf: (userId) => byUserId.get(userId)?.Email,
        departments,
      }),
    [t, byUserId, departments]
  );

  return (
    <AdvanceDataTable
      {...tableProps}
      data={items}
      columns={columns}
      isLoading={isLoading}
      error={error instanceof Error ? error : null}
      pagination={{
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        totalCount,
      }}
      isExpandRowContent={false}
      tableClassName="min-w-[36rem]"
      mobileView="cards"
      mobileActions={mobileActions}
    />
  );
};

/**
 * Meal Orders (HR Module Navigation Map; was Manage Lunch): one day's orders, each employee and the menu
 * they chose, with Export CSV and Print. Tabs Lunch / Dinner (Soon) in the URL (`?tab=`). The menus
 * moved to Settings › Meals › Menu.
 */
export const ManageLunchPage = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [tab, setTab] = useUrlTab<MealTab>([MEAL_TABS.LUNCH, MEAL_TABS.DINNER], MEAL_TABS.LUNCH);
  const [date, setDate] = useState(() => todayInOrganization());
  const isMobile = useIsMobile();
  const datePicker = (
    // On a phone it fills the cards' line, beside the (same height) filter button.
    <DatePicker className="w-full md:w-48" aria-label={t('DATE')} value={date} onChange={setDate} />
  );

  // Every order of the selected day, not just the page on screen.
  const exportFoodRequests = async () => {
    const requests = await getAllLunchRequests(dayFilterOf(date));
    downloadCsv(`Food Request List_${date}`, buildFoodRequestsCsv(requests));
  };
  const printFoodRequestList = async () => {
    const requests = await getAllLunchRequests(dayFilterOf(date));
    const printed = printFoodRequests(
      buildFoodRequestsPrintHtml(`${t('MEAL_ORDERS')} · ${formatDateOnly(date)}`, requests, {
        name: t('NAME'),
        unit: t('DEPARTMENT'),
        menu: t('MENU'),
        total: t('TOTAL'),
      })
    );
    if (!printed) toast({ variant: 'destructive', title: t('MEAL_PRINT_BLOCKED') });
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('MEAL_ORDERS')} />

      <Tabs value={tab} onValueChange={(value) => setTab(value as MealTab)}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value={MEAL_TABS.LUNCH} className="flex-1 sm:flex-none">
              {t('LUNCH')}
            </TabsTrigger>
            <TabsTrigger value={MEAL_TABS.DINNER} className="flex-1 gap-1.5 sm:flex-none">
              {t('DINNER')}
              <SoonBadge />
            </TabsTrigger>
          </TabsList>
          {tab === MEAL_TABS.LUNCH && (
            // On a phone the day picker moves onto the cards' line, beside the filter button.
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button variant="outline" className="sm:w-auto" onClick={printFoodRequestList}>
                <Printer className="size-4" />
                {t('PRINT')}
              </Button>
              <ExportCsvButton onExport={exportFoodRequests} className="sm:w-auto" />
              {!isMobile && datePicker}
            </div>
          )}
        </div>
        <TabsContent value={MEAL_TABS.LUNCH} className="mt-4">
          <FoodRequestsTab date={date} mobileActions={isMobile ? datePicker : undefined} />
        </TabsContent>
        <TabsContent value={MEAL_TABS.DINNER} className="mt-4">
          <SoonPanel name="DINNER" />
        </TabsContent>
      </Tabs>
    </div>
  );
};
